/**
 * Lightweight client error + Core Web Vitals monitoring.
 *
 * - If REACT_APP_SENTRY_DSN is set, events are POSTed directly to Sentry's HTTP
 *   ingest endpoint (no SDK, so zero bundle cost when unused).
 * - Otherwise it falls back to console output in development and a no-op in
 *   production.
 *
 * Privacy: payloads contain only the error message/type/stack, the page
 * pathname (query string + hash stripped), the build target and release. No
 * user identifiers, form values, cookies or query params are ever sent.
 */

const DSN = process.env.REACT_APP_SENTRY_DSN || '';
const RELEASE = process.env.REACT_APP_VERSION || 'dev';
const ENVIRONMENT = process.env.REACT_APP_BUILD_TARGET || 'app';
const IS_PROD = process.env.NODE_ENV === 'production';

interface ParsedDsn {
  publicKey: string;
  host: string;
  projectId: string;
}

function parseDsn(dsn: string): ParsedDsn | null {
  try {
    // https://<publicKey>@<host>/<projectId>
    const url = new URL(dsn);
    const projectId = url.pathname.replace(/^\//, '');
    if (!url.username || !url.host || !projectId) return null;
    return { publicKey: url.username, host: url.host, projectId };
  } catch {
    return null;
  }
}

const parsedDsn = DSN ? parseDsn(DSN) : null;

// uuid4 without dashes, for the Sentry event_id.
function eventId(): string {
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < 32; i++) out += hex[Math.floor(Math.random() * 16)];
  return out;
}

function safeUrl(): string {
  if (typeof window === 'undefined') return '';
  // Strip query + hash to avoid leaking tokens/PII.
  return window.location.origin + window.location.pathname;
}

function postToSentry(body: Record<string, unknown>): void {
  if (!parsedDsn) return;
  const endpoint = `https://${parsedDsn.host}/api/${parsedDsn.projectId}/store/?sentry_key=${parsedDsn.publicKey}&sentry_version=7`;
  try {
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_id: eventId(),
        timestamp: new Date().toISOString(),
        platform: 'javascript',
        release: RELEASE,
        environment: ENVIRONMENT,
        request: { url: safeUrl() },
        ...body,
      }),
      keepalive: true,
    }).catch(() => {
      /* swallow: monitoring must never throw into the app */
    });
  } catch {
    /* ignore */
  }
}

export function reportError(error: unknown, context?: string): void {
  const err = error instanceof Error ? error : new Error(String(error));

  if (parsedDsn) {
    postToSentry({
      level: 'error',
      exception: {
        values: [
          {
            type: err.name || 'Error',
            value: err.message,
            stacktrace: err.stack ? { frames: [] } : undefined,
          },
        ],
      },
      extra: { context, stack: err.stack },
    });
    return;
  }

  if (!IS_PROD) {
    // Dev visibility; production stays silent when no DSN is configured.
    // eslint-disable-next-line no-console
    console.error('[monitoring]', context || 'error', err);
  }
}

function reportMetric(metric: { name: string; value: number; rating?: string; id: string }): void {
  if (parsedDsn) {
    postToSentry({
      level: 'info',
      message: `web-vital ${metric.name}`,
      tags: { metric: metric.name, rating: metric.rating || 'n/a' },
      extra: { value: metric.value, id: metric.id },
    });
    return;
  }

  if (!IS_PROD) {
    // eslint-disable-next-line no-console
    console.debug(`[web-vitals] ${metric.name}: ${Math.round(metric.value)} (${metric.rating ?? 'n/a'})`);
  }
}

/**
 * Install global JS error + unhandled-rejection handlers and start Core Web
 * Vitals reporting. Safe to call once at app startup; never throws.
 */
export function initMonitoring(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, 'window.onerror');
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'unhandledrejection');
  });

  // Core Web Vitals — loaded lazily so it never blocks first paint.
  import('web-vitals')
    .then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
      onCLS(reportMetric);
      onINP(reportMetric);
      onLCP(reportMetric);
      onFCP(reportMetric);
      onTTFB(reportMetric);
    })
    .catch(() => {
      /* web-vitals optional; ignore load failure */
    });
}
